import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { User, Phone, CreditCard, Shield, X, ChevronLeft } from 'lucide-react';
import ProfileTab from './ProfileTab';
import ContactTab from './ContactTab';
import BillingTab from './BillingTab';
import SecurityTab from './SecurityTab';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountSettingsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const tabs = [
    { id: 'profile', label: 'Profil', icon: User },
    { id: 'contact', label: 'Kontakt', icon: Phone },
    { id: 'billing', label: 'Zahlungen & Abo', icon: CreditCard },
    { id: 'security', label: 'Sicherheit', icon: Shield }
  ];

  useEffect(() => {
    if (isOpen) {
      fetchUserData();
    }
  }, [isOpen]);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    switch (activeTab) {
      case 'profile':
        return <ProfileTab user={user} onUserUpdate={fetchUserData} />;
      case 'contact':
        return <ContactTab user={user} onUserUpdate={fetchUserData} />;
      case 'billing':
        return <BillingTab />;
      case 'security':
        return <SecurityTab />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl md:max-h-[90vh] h-full md:h-auto w-full md:w-auto p-0 overflow-hidden md:rounded-lg rounded-none [&>button]:hidden">
        {/* Desktop Layout: Sidebar + Content */}
        <div className="hidden md:flex h-full">
          {/* Desktop Sidebar */}
          <div className="w-64 bg-gray-50 border-r flex flex-col">
            <DialogHeader className="p-6 pb-4 border-b">
              <DialogTitle className="text-xl">Account Einstellungen</DialogTitle>
            </DialogHeader>

            <nav className="flex-1 p-4 space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>

            <div className="p-4 border-t">
              <button
                onClick={onClose}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                Schließen
              </button>
            </div>
          </div>

          {/* Desktop Content Area */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-8">
              {renderTabContent()}
            </div>
          </div>
        </div>

        {/* Mobile Layout: Full-Screen with Top Tabs */}
        <div className="flex md:hidden flex-col h-full">
          {/* Mobile Header with Back Button */}
          <div className="flex items-center gap-3 px-4 py-4 border-b bg-white sticky top-0 z-10">
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Zurück"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900">Account Einstellungen</h2>
          </div>

          {/* Mobile Horizontal Tabs */}
          <div className="border-b bg-white overflow-x-auto scrollbar-hide">
            <nav className="flex px-2 py-2 gap-1 min-w-max">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg whitespace-nowrap transition-all ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Mobile Content Area */}
          <div className="flex-1 overflow-y-auto bg-white">
            <div className="p-4">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccountSettingsModal;
